import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_LEGAL_SETTINGS, LegalSettings } from '../services/queries';
import { Building2, UserCheck, Target, Database, Lock, Globe, Clock, Shield, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface Section {
  id: string;
  anchor?: string;
  title: string;
  icon: React.ReactNode;
  show?: boolean;
  content: React.ReactNode;
}

const PrivacyPolicyPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['1']);
  const { data } = useQuery<{ legalSettings: LegalSettings }>(GET_LEGAL_SETTINGS);
  const location = useLocation();
  const legal = data?.legalSettings;

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  // Gestione hash link (#diritti-interessato)
  useEffect(() => {
    if (location.hash === '#diritti-interessato') {
      setExpandedSections(prev => prev.includes('8') ? prev : [...prev, '8']);
      setTimeout(() => {
        const el = document.getElementById('diritti-interessato');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.hash]);

  const sections: Section[] = [
    {
      id: '1',
      title: '1. Titolare del trattamento',
      icon: <Building2 className="w-5 h-5" />,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Titolare del trattamento dei Suoi dati personali ai sensi del Regolamento (UE) 2016/679 è{' '}
            <strong>{legal?.legalEntityName || '[Da configurare in Impostazioni]'}</strong>, in persona del{' '}
            {legal?.legalRepresentative || '[Rappresentante legale]'}, con sede legale in{' '}
            {legal?.legalEntityAddress || '[Indirizzo]'}.
          </p>
          <ul className="list-none space-y-1">
            {legal?.legalEntityPhone && <li><strong>Tel:</strong> {legal.legalEntityPhone}</li>}
            {legal?.legalEntityFax && <li><strong>Fax:</strong> {legal.legalEntityFax}</li>}
            {legal?.legalEntityEmail && <li><strong>Email:</strong> <a href={`mailto:${legal.legalEntityEmail}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.legalEntityEmail}</a></li>}
            {legal?.legalEntityPec && <li><strong>PEC:</strong> <a href={`mailto:${legal.legalEntityPec}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.legalEntityPec}</a></li>}
            {legal?.vatNumber && <li><strong>P.IVA:</strong> {legal.vatNumber}</li>}
            {legal?.fiscalCode && <li><strong>C.F.:</strong> {legal.fiscalCode}</li>}
          </ul>
        </div>
      ),
    },
    {
      id: '2',
      title: '2. Responsabile della Protezione dei Dati (DPO)',
      icon: <UserCheck className="w-5 h-5" />,
      show: !!legal?.dpoName,
      content: (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Il Responsabile della Protezione dei Dati (DPO) nominato dal Titolare è{' '}
            <strong>{legal?.dpoName}</strong>.
          </p>
          <ul className="list-none space-y-1">
            {legal?.dpoEmail && <li><strong>Email:</strong> <a href={`mailto:${legal.dpoEmail}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.dpoEmail}</a></li>}
            {legal?.dpoPhone && <li><strong>Tel:</strong> {legal.dpoPhone}</li>}
          </ul>
        </div>
      ),
    },
    {
      id: '3',
      title: `${legal?.dpoName ? '3' : '2'}. Finalità del trattamento e base giuridica`,
      icon: <Target className="w-5 h-5" />,
      content: (
        <div className="space-y-5 text-sm text-gray-700 dark:text-gray-300">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">a) Gestione del rapporto associativo — Art. 6.1.b GDPR (esecuzione contratto)</h4>
            <p>
              I dati personali conferiti sono trattati per il perseguimento degli scopi statutari e lo svolgimento delle finalità
              associative: iscrizione, gestione della tessera, comunicazioni associative, partecipazione ad eventi, attività di
              formazione, invio di materiale informativo e newsletter relative alle iniziative promosse dal Titolare.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">b) Adempimenti legali e fiscali — Art. 6.1.c GDPR (obbligo legale)</h4>
            <p>
              Il trattamento è necessario per adempiere obblighi di legge in materia contabile, fiscale e di conservazione
              documentale ai quali è soggetto il Titolare.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">c) Comunicazioni promozionali — Art. 6.1.a GDPR (consenso)</h4>
            <p>
              Previo specifico consenso dell'interessato, i dati potranno essere trattati per finalità di informazione e
              promozione di prodotti, servizi, eventi e iniziative del Titolare, attraverso strumenti tradizionali (posta, telefono)
              e/o elettronici (email, SMS, app, social media). Il consenso è revocabile in qualsiasi momento tramite il
              link di disiscrizione presente in ogni comunicazione o contattando il Titolare.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">d) Funzionamento della piattaforma e sicurezza — Art. 6.1.f GDPR (legittimo interesse)</h4>
            <p>
              Il trattamento è necessario per il legittimo interesse del Titolare a garantire il corretto funzionamento della
              piattaforma, la sicurezza informatica, la prevenzione di abusi e la gestione dei log tecnici.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: '4',
      title: `${legal?.dpoName ? '4' : '3'}. Categorie di dati trattati`,
      icon: <Database className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <ul className="space-y-3">
            <li><strong>Dati anagrafici:</strong> nome, cognome, data e luogo di nascita, indirizzo di residenza.</li>
            <li><strong>Dati di contatto:</strong> email, telefono, fax, PEC.</li>
            <li><strong>Dati fiscali e aziendali:</strong> partita IVA, codice fiscale, ragione sociale, sede legale e operativa.</li>
            <li><strong>Dati di navigazione:</strong> indirizzo IP (anonimizzato), tipo di browser, pagine visitate, durata della visita (tramite Google Analytics 4, solo previo consenso).</li>
            <li><strong>Dati di pagamento:</strong> elaborati direttamente da Stripe Inc. Il Titolare non conserva dati di carte di credito.</li>
            <li><strong>Dati della community:</strong> messaggi, post e commenti pubblicati nell'area community della piattaforma.</li>
            <li><strong>Dati di geolocalizzazione:</strong> coordinate approssimative della sede (per la mappa della Rete Certificata), derivate dall'indirizzo tramite OpenStreetMap/Nominatim.</li>
            <li><strong>Dati push notification:</strong> endpoint tecnico del browser per la ricezione di notifiche push (non associato a dati personali identificativi).</li>
          </ul>
        </div>
      ),
    },
    {
      id: '5',
      title: `${legal?.dpoName ? '5' : '4'}. Modalità di trattamento`,
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            Il trattamento dei dati personali viene effettuato con l'ausilio di mezzi elettronici e informatici, con modalità
            e procedure strettamente necessarie al perseguimento delle finalità sopra descritte.
          </p>
          <p>
            Il Titolare adotta misure di sicurezza tecniche e organizzative adeguate a proteggere i dati personali, tra cui:
            crittografia delle comunicazioni, controllo degli accessi basato su ruoli, backup periodici, autenticazione a
            più fattori per gli operatori, registrazione degli accessi (log).
          </p>
          <p>
            Il trattamento è effettuato da personale autorizzato e debitamente istruito dal Titolare.
          </p>
        </div>
      ),
    },
    {
      id: '6',
      title: `${legal?.dpoName ? '6' : '5'}. Destinatari e trasferimenti dati`,
      icon: <Globe className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-4">
          <p>I dati personali potranno essere comunicati ai seguenti soggetti terzi, in qualità di responsabili del trattamento:</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Servizio</th>
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Fornitore</th>
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Finalità</th>
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Paese</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Pagamenti</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Stripe Inc.</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Elaborazione pagamenti</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">USA (CCS)</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Analytics</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Google LLC (GA4)</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Statistiche di navigazione anonimizzate</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">USA (CCS)</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Email</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">TurboSMTP (Delivery Tech)</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Invio comunicazioni</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Italia/UE</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Mappe</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">OpenStreetMap Foundation</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Geocoding indirizzi sedi</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">UE</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            I dati personali non sono soggetti a diffusione. I trasferimenti extra-UE avvengono sulla base di Clausole
            Contrattuali Standard (art. 46.2.c GDPR) o decisioni di adeguatezza della Commissione Europea.
          </p>
        </div>
      ),
    },
    {
      id: '7',
      title: `${legal?.dpoName ? '7' : '6'}. Periodo di conservazione`,
      icon: <Clock className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <ul className="space-y-2">
            <li><strong>Dati associativi:</strong> per tutta la durata del rapporto associativo.</li>
            <li><strong>Dati fiscali e contabili:</strong> 10 anni dalla cessazione del rapporto (obbligo legale).</li>
            <li><strong>Dati promozionali:</strong> fino a revoca del consenso, e comunque non oltre 24 mesi dalla cessazione del rapporto associativo.</li>
            <li><strong>Dati di navigazione (analytics):</strong> 14 mesi (configurazione GA4 predefinita).</li>
            <li><strong>Dati community:</strong> per la durata dell'iscrizione, cancellati su richiesta.</li>
            <li><strong>Log tecnici e sicurezza:</strong> 12 mesi.</li>
          </ul>
          <p className="mt-3">Al termine del periodo di conservazione i dati personali saranno cancellati ovvero resi anonimi.</p>
        </div>
      ),
    },
    {
      id: '8',
      anchor: 'diritti-interessato',
      title: `${legal?.dpoName ? '8' : '7'}. Diritti dell'interessato`,
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-4">
          <p>Ai sensi degli artt. 15-22 del Regolamento (UE) 2016/679, l'interessato ha il diritto di:</p>
          <ul className="space-y-2">
            <li><strong>Accesso</strong> (art. 15) — ottenere conferma dell'esistenza di un trattamento e accedere ai propri dati personali.</li>
            <li><strong>Rettifica</strong> (art. 16) — ottenere la correzione di dati inesatti o l'integrazione di dati incompleti.</li>
            <li><strong>Cancellazione</strong> (art. 17) — ottenere la cancellazione dei propri dati ("diritto all'oblio"), nei casi previsti dal Regolamento.</li>
            <li><strong>Limitazione del trattamento</strong> (art. 18) — ottenere la limitazione del trattamento nei casi previsti.</li>
            <li><strong>Portabilità</strong> (art. 20) — ricevere i propri dati in formato strutturato e leggibile da dispositivo automatico.</li>
            <li><strong>Opposizione</strong> (art. 21) — opporsi al trattamento per motivi legati alla propria situazione particolare.</li>
            <li><strong>Revoca del consenso</strong> (art. 7.3) — revocare in qualsiasi momento il consenso prestato, senza pregiudicare la liceità del trattamento basata sul consenso prestato prima della revoca.</li>
            <li><strong>Reclamo</strong> — proporre reclamo al Garante per la protezione dei dati personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">www.garanteprivacy.it</a>).</li>
          </ul>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Come esercitare i diritti</p>
            <p>
              Per esercitare i Suoi diritti, può scrivere al Titolare all'indirizzo{' '}
              {legal?.legalEntityEmail ? (
                <a href={`mailto:${legal.legalEntityEmail}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.legalEntityEmail}</a>
              ) : '[Da configurare]'}
              {legal?.legalEntityPec && (
                <> oppure via PEC a <a href={`mailto:${legal.legalEntityPec}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.legalEntityPec}</a></>
              )}
              {legal?.legalEntityAddress && (
                <>, oppure inviare comunicazione scritta a: {legal.legalEntityAddress}.</>
              )}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: '9',
      title: `${legal?.dpoName ? '9' : '8'}. Modifiche all'informativa`,
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <p>
            Il Titolare si riserva di modificare la presente informativa in qualsiasi momento. Eventuali modifiche saranno
            pubblicate su questa pagina. La data dell'ultimo aggiornamento è indicata in fondo alla pagina.
          </p>
        </div>
      ),
    },
  ];

  const visibleSections = sections.filter(s => s.show !== false);

  return (
    <div className="container-custom py-8 legal-content">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white mb-2">Informativa Privacy</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Informativa sul trattamento dei dati personali ai sensi dell'art. 13 del Regolamento (UE) 2016/679 (GDPR)
          </p>
        </div>

        {/* Sezioni Accordion */}
        <div className="space-y-4">
          {visibleSections.map((section) => (
            <div
              key={section.id}
              id={section.anchor}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-indigo-600 dark:text-indigo-400">{section.icon}</div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{section.title}</h2>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.includes(section.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                  <div className="pt-4">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Usa l'app UNASCA Studi per iOS o Android? Legga anche l'<Link to="/privacy-app" className="text-primary-600 dark:text-primary-400 hover:underline">informativa privacy dell'app</Link>.
        </p>

        {/* Footer aggiornamento */}
        <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
          <p>Ultimo aggiornamento: 4 aprile 2026</p>
          <p className="mt-1">
            Riferimenti normativi: Regolamento (UE) 2016/679 (GDPR) · D.Lgs. 196/2003 · D.Lgs. 101/2018
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
